import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Chip,
  Stack,
  Tabs,
  Tab,
  AvatarGroup,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import GroupIcon from '@mui/icons-material/Group';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import useApi from 'hooks/useApi';
import { ChatThread, ChatParticipant, ChatAttachment, ChatGroup } from 'types/chat';
import ChatEditor from './ChatEditor';
import { htmlToMarkdown } from './chatUtils';

interface StartChatPanelProps {
  onBack: () => void;
  transactionId: string;
  transactionKey?: string;
  onChatCreated: (thread: ChatThread) => void;
  embedded?:boolean
  /**
   * Quick Chat: narrow the People roster to these user uids instead of the whole
   * team. The transaction Details page passes the users on that transaction —
   * assigned via a step's RESOURCE field, or having submitted a step — so opening
   * the Communication panel lands straight on them. The rest of the team stays one
   * "Show all users" click away. Openers that pass nothing (or an empty list) get
   * the unchanged full-team roster.
   *
   * Mirrors `restrictToUids` on the Quick Call dialog, and matches on the same
   * key: `getChatParticipants` and `getCallCandidates` both return
   * ResourceData.uid.
   */
  restrictToUids?: string[];
}

const StartChatPanel = ({
  onBack,
  transactionId,
  transactionKey,
  onChatCreated,
  embedded=false,
  restrictToUids
}: StartChatPanelProps) => {
  const { getChatParticipants, createChatThread, checkExistingChatThread, getChatGroups, createChatGroup, updateChatGroup, deleteChatGroup, uploadChatFiles, showToast } = useApi();

  const [participants, setParticipants] = useState<ChatParticipant[]>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const [messageHtml, setMessageHtml] = useState('');
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [existingThread, setExistingThread] = useState<any>(null);

  // Attachment state for initial message
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Quick Chat escape hatch: when the opener restricted the roster, this lets the
  // user fall back to the full team without leaving the panel.
  const [showAll, setShowAll] = useState(false);

  // Groups state
  const [groups, setGroups] = useState<ChatGroup[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [groupMemberSearch, setGroupMemberSearch] = useState('');

  // Create/Edit group state
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ChatGroup | null>(null);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState<string[]>([]);
  const [savingGroup, setSavingGroup] = useState(false);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuGroup, setMenuGroup] = useState<ChatGroup | null>(null);

  const loadParticipants = useCallback(async () => {
    if (!transactionId) return;
    setLoading(true);
    try {
      const response = await getChatParticipants(transactionId);
      if (response.success) setParticipants(response.data || []);
    } catch (error) {
      console.error('Error loading participants:', error);
    } finally {
      setLoading(false);
    }
  }, [transactionId, getChatParticipants]);

  const loadGroups = useCallback(async () => {
    if (!transactionId) return;
    setLoadingGroups(true);
    try {
      const groupsRes = await getChatGroups(transactionId);
      if (groupsRes.success) setGroups(groupsRes.data || []);
    } catch (error) {
      console.error('Error loading groups:', error);
    } finally {
      setLoadingGroups(false);
    }
  }, [transactionId, getChatGroups]);

  useEffect(() => {
    loadParticipants();
    loadGroups();
  }, [loadParticipants, loadGroups]);

  // Re-arm the Quick Chat restriction when the panel is pointed at a different
  // transaction. It resets on remount anyway (the Communication drawer mounts the
  // panel fresh each time), but the panel is kept mounted on the Edit Event
  // dialog, where a "show all" from a previous transaction would otherwise stick.
  useEffect(() => {
    setShowAll(false);
  }, [transactionId]);

  const handleToggleParticipant = (uid: string) => {
    setSelectedParticipants(prev =>
      prev.includes(uid) ? prev.filter(p => p !== uid) : [...prev, uid]
    );
    setExistingThread(null);
  };

  const handleRemoveParticipant = (uid: string) => {
    setSelectedParticipants(prev => prev.filter(p => p !== uid));
    setExistingThread(null);
  };

  const handleSelectGroup = (members: ChatParticipant[]) => {
    const memberUids = members.map(m => m.uid);
    setSelectedParticipants(prev => {
      const combined = new Set([...prev, ...memberUids]);
      return Array.from(combined);
    });
    setExistingThread(null);
    showToast(`Added ${members.length} members`, 'success');
  };

  // File attachment helpers
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      setPendingFiles(prev => [...prev, ...fileArray]);
    }
    setTimeout(() => { if (fileInputRef.current) fileInputRef.current.value = ''; }, 0);
  };

  const handleRemoveFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const [isDragOver, setIsDragOver] = useState(false);
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragOver(false);
    if (e.dataTransfer.files) setPendingFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]);
  };

  const isImageFile = (type: string) => type?.startsWith('image/');

  const getFileIconForType = (type: string) => {
    if (isImageFile(type)) return <ImageIcon sx={{ fontSize: 14 }} />;
    if (type === 'application/pdf') return <PictureAsPdfIcon sx={{ fontSize: 14 }} />;
    return <InsertDriveFileIcon sx={{ fontSize: 14 }} />;
  };

  const handleSend = async () => {
    if (selectedParticipants.length === 0) { showToast('Please select at least one participant', 'warning'); return; }
    const markdown = htmlToMarkdown(messageHtml);
    const messageText = markdown || message.trim();
    if (!messageText && pendingFiles.length === 0) { showToast('Please enter a message or attach a file', 'warning'); return; }

    setSending(true);

    // Check if a thread already exists with these exact participants
    try {
      const checkResponse = await checkExistingChatThread(transactionId, selectedParticipants);
      if (checkResponse.success && checkResponse.data?.exists && checkResponse.data?.thread) {
        setExistingThread(checkResponse.data.thread);
        setSending(false);
        return;
      }
    } catch {
      // If check fails, proceed with creation
    }

    // Upload files if any
    let attachments: ChatAttachment[] = [];
    if (pendingFiles.length > 0) {
      setUploading(true);
      try {
        console.log('Uploading files:3', pendingFiles);
        const uploadResponse = await uploadChatFiles(pendingFiles);
        if (uploadResponse.success) {
          attachments = uploadResponse.data;
        } else {
          showToast('Failed to upload files', 'error');
          setSending(false); setUploading(false); return;
        }
      } catch {
        showToast('Failed to upload files', 'error');
        setSending(false); setUploading(false); return;
      }
      setUploading(false);
    }

    const finalMessage = messageText || (attachments.length > 0 ? `Sent ${attachments.length} file${attachments.length > 1 ? 's' : ''}` : '');

    try {
      const response = await createChatThread({
        transactionId,
        subject: subject || `Chat for ${transactionKey || transactionId}`,
        participants: selectedParticipants,
        initialMessage: finalMessage,
        initialAttachments: attachments.length > 0 ? attachments : undefined,
      });
      if (response.success) {
        showToast('Chat started successfully', 'success');
        onChatCreated(response.data);
      } else {
        showToast(response.message || 'Failed to start chat', 'error');
      }
    } catch (error) {
      showToast('Failed to start chat', 'error');
    } finally {
      setSending(false);
    }
  };

  // Group CRUD
  const handleOpenCreateGroup = () => {
    setShowCreateGroup(true); setEditingGroup(null); setGroupName(''); setGroupMembers([]);
  };

  const handleEditGroup = (group: ChatGroup) => {
    setShowCreateGroup(true); setEditingGroup(group);
    setGroupName(group.name); setGroupMembers(group.members.map(m => m.uid));
    setMenuAnchor(null);
  };

  const handleSaveGroup = async () => {
    if (!groupName.trim()) { showToast('Enter a group name', 'warning'); return; }
    if (groupMembers.length === 0) { showToast('Select at least one member', 'warning'); return; }
    setSavingGroup(true);
    try {
      if (editingGroup) {
        const res = await updateChatGroup(editingGroup.uid, { name: groupName.trim(), members: groupMembers });
        if (res.success) { showToast('Group updated', 'success'); loadGroups(); }
      } else {
        const res = await createChatGroup(transactionId, { name: groupName.trim(), members: groupMembers });
        if (res.success) { showToast('Group created', 'success'); loadGroups(); }
      }
      setShowCreateGroup(false); setEditingGroup(null);
    } catch { showToast('Failed to save group', 'error'); }
    finally { setSavingGroup(false); }
  };

  const handleDeleteGroup = async (group: ChatGroup) => {
    setMenuAnchor(null);
    try {
      const res = await deleteChatGroup(group.uid);
      if (res.success) { showToast('Group deleted', 'success'); loadGroups(); }
    } catch { showToast('Failed to delete group', 'error'); }
  };

  // Quick Chat: when the opener passed a restriction (the Details page passes the
  // users on that transaction — assigned on a step, or having submitted one), the
  // People roster is narrowed to them. Anyone not in that set is hidden until the
  // user asks for everyone. Scoped before the search filter, so searching searches
  // within the restriction.
  const restrictSet =
    restrictToUids && restrictToUids.length > 0 ? new Set(restrictToUids.map(String)) : null;
  const restricting = !!restrictSet && !showAll;
  const scopedParticipants = restricting
    ? participants.filter(p => restrictSet!.has(String(p.uid)))
    : participants;
  const hiddenCount = restricting ? participants.length - scopedParticipants.length : 0;

  const filteredParticipants = scopedParticipants.filter(p => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const fullName = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
    return fullName.includes(query) || (p.email && p.email.toLowerCase().includes(query)) || (p.resourceType && p.resourceType.toLowerCase().includes(query));
  });

  const filteredGroups = groups.filter(g => !groupSearchQuery || g.name.toLowerCase().includes(groupSearchQuery.toLowerCase()));

  // Deliberately off the unscoped roster: a selected user's chip must keep
  // rendering even when the Quick Chat restriction hides them from the list
  // (group selection can add members from outside the restricted set).
  const getSelectedParticipantDetails = () => participants.filter(p => selectedParticipants.includes(p.uid));

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  };

  // Group create/edit form. Deliberately unrestricted — groups are process-scoped
  // and reused across transactions, so building one must draw on the whole team,
  // not just whoever happens to be assigned on the transaction in view.
  const filteredGroupMembers = participants.filter(p => {
    if (!groupMemberSearch) return true;
    const q = groupMemberSearch.toLowerCase();
    const name = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
    return name.includes(q) || (p.email || '').toLowerCase().includes(q);
  });

  const renderGroupForm = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ px: 2, pt: 2, pb: 1, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <IconButton size="small" onClick={() => { setShowCreateGroup(false); setEditingGroup(null); setGroupMemberSearch(''); }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography variant="subtitle2" fontWeight={600}>
            {editingGroup ? 'Edit Group' : 'Create Group'}
          </Typography>
        </Box>
        <TextField fullWidth size="small" label="Group Name" placeholder="e.g. Reviewers, Field Team..."
          value={groupName} onChange={(e) => setGroupName(e.target.value)} />
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, mx: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, mt: 1 }}>
          Select Members ({groupMembers.length} selected)
        </Typography>
        <TextField fullWidth size="small" placeholder="Search members..."
          value={groupMemberSearch} onChange={(e) => setGroupMemberSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" sx={{ fontSize: 16 }} /></InputAdornment> }}
          sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'grey.50' } }}
        />
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <List dense disablePadding>
          {filteredGroupMembers.map((p) => {
            const isSelected = groupMembers.includes(p.uid);
            const displayName = `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.email || 'User';
            return (
              <ListItemButton key={p.uid} onClick={() => setGroupMembers(prev =>
                prev.includes(p.uid) ? prev.filter(u => u !== p.uid) : [...prev, p.uid]
              )} sx={{ py: 0.75, bgcolor: isSelected ? 'primary.50' : 'transparent' }}>
                <Checkbox checked={isSelected} size="small" sx={{ p: 0.5, mr: 1 }} />
                <ListItemAvatar sx={{ minWidth: 36 }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: 11,
                    bgcolor: isSelected ? 'primary.main' : 'primary.light',
                    color: isSelected ? 'white' : 'primary.main' }}>
                    {getInitials(p.firstName, p.lastName)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={displayName}
                  secondary={p.email || p.resourceType}
                  primaryTypographyProps={{ variant: 'body2', fontSize: 13, fontWeight: isSelected ? 600 : 400 }}
                  secondaryTypographyProps={{ variant: 'caption', fontSize: 10 }}
                />
              </ListItemButton>
            );
          })}
          {filteredGroupMembers.length === 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" fontSize={12}>No members found</Typography>
            </Box>
          )}
        </List>
        </Box>
      </Box>
      <Box sx={{ p: 2, flexShrink: 0 }}>
        <Button variant="contained" fullWidth size="small"
          startIcon={savingGroup ? <CircularProgress size={12} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
          onClick={handleSaveGroup} disabled={savingGroup || !groupName.trim() || groupMembers.length === 0}
          sx={{ textTransform: 'none', fontWeight: 600 }}>
          {savingGroup ? 'Saving...' : (editingGroup ? 'Update Group' : 'Create Group')}
        </Button>
      </Box>
    </Box>
  );

  // Groups tab content
  const renderGroupsTab = () => {
    if (showCreateGroup) return renderGroupForm();
    return (
      <Box>
        <Box sx={{ p: 1.5, display: 'flex', gap: 1 }}>
          <TextField fullWidth size="small" placeholder="Search groups..."
            value={groupSearchQuery} onChange={(e) => setGroupSearchQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" sx={{ fontSize: 16 }} /></InputAdornment> }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: 'grey.100' } }}
          />
          <Tooltip title="Create Group">
            <Button variant="outlined" size="small" onClick={handleOpenCreateGroup} sx={{ minWidth: 36, px: 1 }}>
              <AddIcon sx={{ fontSize: 18 }} />
            </Button>
          </Tooltip>
        </Box>

        {loadingGroups ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>
        ) : (
          <>
            {filteredGroups.length > 0 && (
              <>
                <Typography variant="caption" sx={{ px: 1.5, py: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600, bgcolor: 'grey.50', fontSize: 11 }}>
                  My Groups ({filteredGroups.length})
                </Typography>
                <List dense disablePadding>
                  {filteredGroups.map((group) => (
                    <ListItem key={group.uid} disablePadding
                      secondaryAction={
                        <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuGroup(group); }}>
                          <MoreVertIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      }>
                      <ListItemButton onClick={() => handleSelectGroup(group.members)} sx={{ pr: 5, py: 0.75 }}>
                        <ListItemAvatar sx={{ minWidth: 40 }}>
                          <Avatar sx={{ width: 30, height: 30, bgcolor: 'primary.light', color: 'primary.main' }}>
                            <GroupIcon sx={{ fontSize: 16 }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography variant="body2" fontWeight={600} fontSize={13}>{group.name}</Typography>}
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                              <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 18, height: 18, fontSize: 8 } }}>
                                {group.members.map(m => (
                                  <Tooltip key={m.uid} title={`${m.firstName} ${m.lastName}`}>
                                    <Avatar sx={{ bgcolor: 'grey.400' }}>{getInitials(m.firstName, m.lastName)}</Avatar>
                                  </Tooltip>
                                ))}
                              </AvatarGroup>
                              <Typography variant="caption" color="text.secondary" fontSize={10}>{group.members.length} members</Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {filteredGroups.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <GroupIcon sx={{ fontSize: 36, color: 'grey.300', mb: 1 }} />
                <Typography color="text.secondary" variant="body2" fontSize={13}>No groups yet</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={handleOpenCreateGroup} sx={{ mt: 1, textTransform: 'none', fontSize: 12 }}>
                  Create your first group
                </Button>
              </Box>
            )}
          </>
        )}

        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={() => menuGroup && handleEditGroup(menuGroup)} sx={{ fontSize: 12 }}>
            <EditIcon sx={{ fontSize: 14, mr: 1 }} /> Edit
          </MenuItem>
          <MenuItem onClick={() => menuGroup && handleDeleteGroup(menuGroup)} sx={{ fontSize: 12, color: 'error.main' }}>
            <DeleteIcon sx={{ fontSize: 14, mr: 1 }} /> Delete
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      {/* Header */}
      <Box
        sx={{
          p: 0.8, bgcolor: 'primary.main', color: 'white',
          display: 'flex', alignItems: 'center', gap: 1,
          borderRadius: embedded ? '8px 8px 0 0' : 0, flexShrink: 0,
          margin: embedded ? '10px' : 0
        }}
      >
        {!embedded && (
          <IconButton onClick={onBack} sx={{ color: 'white', p: 0 }}><ArrowBackIcon /></IconButton>
        )}
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {embedded ? 'Start Quick Chat' : 'New Message'}
          </Typography>
          {transactionKey && !embedded && (
            <Typography variant="caption" sx={{ opacity: 0.85 }}>{transactionKey}</Typography>
          )}
        </Box>
      </Box>

      {/* Subject Input */}
      <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <TextField fullWidth size="small" placeholder="Chat Subject (optional)"
          value={subject} onChange={(e) => setSubject(e.target.value)} variant="outlined"
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: 2 } }} />
      </Box>

      {/* Selected Participants */}
      {selectedParticipants.length > 0 && (
        <Box sx={{ px: 2, py: 1, bgcolor: '#e3f2fd', borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: 11 }}>
            TO: {selectedParticipants.length} selected
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
            {getSelectedParticipantDetails().map(p => (
              <Chip key={p.uid} label={`${p.firstName} ${p.lastName}`} size="small"
                onDelete={() => handleRemoveParticipant(p.uid)} color="primary" sx={{ height: 24, fontSize: 11 }} />
            ))}
          </Stack>
        </Box>
      )}

      {/* Tabs: People | Groups */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0, minHeight: 36,
          '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontSize: 12, fontWeight: 600 } }}>
        <Tab label="People" />
        <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <GroupIcon sx={{ fontSize: 14 }} /> Groups
          {groups.length > 0 && <Chip label={groups.length} size="small" sx={{ height: 16, fontSize: 9, ml: 0.5 }} />}
        </Box>} />
      </Tabs>

      {/* Tab Content — scrollable, fills remaining space */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: showCreateGroup ? 'hidden' : 'auto', display: 'flex', flexDirection: 'column' }}>
        {activeTab === 0 ? (
          <>
            {/* People Tab — Search */}
            <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <TextField fullWidth size="small" placeholder="Search participants..."
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" fontSize="small" /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: 'grey.100' } }}
              />
              {!embedded && filteredParticipants.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={() => {
                    const filteredUids = filteredParticipants.map(p => p.uid);
                    const allSelected = filteredUids.every(uid => selectedParticipants.includes(uid));
                    if (allSelected) {
                      setSelectedParticipants(prev => prev.filter(uid => !filteredUids.includes(uid)));
                    } else {
                      setSelectedParticipants(prev => {
                        const newSelection = [...prev];
                        filteredUids.forEach(uid => { if (!newSelection.includes(uid)) newSelection.push(uid); });
                        return newSelection;
                      });
                    }
                  }} sx={{ textTransform: 'none', fontSize: 12, color: 'primary.main', fontWeight: 500 }}>
                    {filteredParticipants.every(p => selectedParticipants.includes(p.uid)) ? 'Deselect All' : `Select All (${filteredParticipants.length})`}
                  </Button>
                </Box>
              )}
            </Box>

            {/* People Tab — List.
                The embedded "search first" placeholder is skipped while a Quick Chat
                restriction is active: the whole point is that the transaction's own
                users are already on screen, so there is nothing to search for first. */}
            {embedded && !searchQuery && !restricting ? (
              <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', minHeight: 300 }}>
                <Box component="img" src={require('assets/images/icons/Messaging.png')} alt="Start a conversation"
                  sx={{ width: 200, height: 200, mb: 0.5, objectFit: 'contain' }} />
                <Typography variant="body2" color="primary.main" sx={{ fontSize: '14px', fontWeight: 500 }}>
                  Search for participants to start messaging
                </Typography>
              </Box>
            ) : loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>
            ) : filteredParticipants.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery
                    ? 'No participants found'
                    : restricting
                      ? 'No users on this transaction are available to chat with'
                      : 'No participants available'}
                </Typography>
              </Box>
            ) : (
              <List disablePadding dense>
                {filteredParticipants.map((participant) => {
                  const isSelected = selectedParticipants.includes(participant.uid);
                  return (
                    <ListItem key={participant.uid} disablePadding
                      sx={{ bgcolor: isSelected ? 'primary.50' : 'transparent', borderBottom: '1px solid', borderColor: 'divider' }}>
                      <ListItemButton onClick={() => handleToggleParticipant(participant.uid)} sx={{ py: 1 }}>
                        <Checkbox edge="start" checked={isSelected} tabIndex={-1} disableRipple size="small" color="primary" />
                        <ListItemAvatar sx={{ minWidth: 44 }}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: isSelected ? 'primary.main' : 'grey.300',
                            color: isSelected ? 'white' : 'grey.700', fontSize: 13, fontWeight: 600 }}>
                            {getInitials(participant.firstName, participant.lastName)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography variant="body2" fontWeight={isSelected ? 600 : 500} sx={{ fontSize: 13 }}>{participant.firstName} {participant.lastName}</Typography>}
                          secondary={
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                              {participant.email && <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: 11, maxWidth: 140 }}>{participant.email}</Typography>}
                              {participant.resourceType && <Chip label={participant.resourceType} size="small" sx={{ height: 18, fontSize: 9, bgcolor: 'grey.200', '& .MuiChip-label': { px: 1 } }} />}
                            </Stack>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}

            {/* Quick Chat escape hatch — only while the restriction is actually
                hiding someone, so an assigned user who isn't a chattable team
                member can't strand the sender. */}
            {!loading && restricting && hiddenCount > 0 && (
              <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
                <Button size="small" onClick={() => setShowAll(true)} sx={{ textTransform: 'none', fontSize: 12 }}>
                  Show all users ({hiddenCount} more)
                </Button>
              </Box>
            )}
          </>
        ) : (
          renderGroupsTab()
        )}
      </Box>

      {/* Existing thread banner */}
      {existingThread && (
        <Box sx={{
          mx: 2, mb: 1, p: 1.5, bgcolor: 'info.50', border: 1, borderColor: 'info.200',
          borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0,
        }}>
          <ChatBubbleIcon sx={{ fontSize: 20, color: 'info.main', flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>
              A chat with these participants already exists
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {existingThread.subject || 'Chat Thread'}
            </Typography>
          </Box>
          <Button size="small" variant="contained" color="info"
            onClick={() => { onChatCreated(existingThread); }}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', borderRadius: 1.5 }}>
            Open Chat
          </Button>
        </Box>
      )}

      {/* Message Input Area */}
      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50', flexShrink: 0, position: 'relative' }}
      >
        {isDragOver && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 10, bgcolor: 'rgba(25, 118, 210, 0.08)',
            border: '2px dashed', borderColor: 'primary.main', borderRadius: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Typography color="primary" fontWeight={600} fontSize={13}>Drop files here</Typography>
          </Box>
        )}
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: 11 }}>First Message</Typography>
            <Tooltip title="Attach files">
              <IconButton size="small" onClick={() => fileInputRef.current?.click()} disabled={sending || uploading}>
                <AttachFileIcon sx={{ fontSize: 16, transform: 'rotate(45deg)' }} />
              </IconButton>
            </Tooltip>
          </Box>
          <input type="file" ref={fileInputRef} onChange={handleFileSelect} multiple style={{ display: 'none' }} />
          <Box sx={{ mt: 0.5 }}>
            <ChatEditor
              onSend={handleSend}
              onContentChange={(text, html) => { setMessage(text); setMessageHtml(html); }}
              onMentionQuery={() => {}}
              onPasteFiles={(files) => setPendingFiles(prev => [...prev, ...files])}
              disabled={sending || uploading}
              placeholder="Type your message..."
              maxRows={embedded ? 4 : 6}
              clearTrigger={0}
            />
          </Box>

          {/* Pending file previews */}
          {pendingFiles.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
              {pendingFiles.map((file, idx) => (
                <Chip key={idx} size="small" variant="outlined"
                  icon={file.type.startsWith('image/')
                    ? <Box component="img" src={URL.createObjectURL(file)} sx={{ width: 16, height: 16, borderRadius: 0.5, objectFit: 'cover' }} />
                    : getFileIconForType(file.type)}
                  label={file.name}
                  onDelete={() => handleRemoveFile(idx)}
                  sx={{ maxWidth: 180, '& .MuiChip-label': { fontSize: 10 } }}
                />
              ))}
            </Box>
          )}

          {uploading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={12} />
              <Typography variant="caption" color="text.secondary" fontSize={10}>Uploading...</Typography>
            </Box>
          )}
        </Box>
        <Stack direction="row" spacing={1} sx={{ px: 2, pb: 2 }}>
          <Button onClick={onBack} variant="outlined" size="small" sx={{ flex: 1, borderRadius: 2, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleSend} variant="contained" size="small"
            disabled={sending || uploading || selectedParticipants.length === 0 || (!message.trim() && pendingFiles.length === 0)}
            startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
            sx={{ flex: 1, borderRadius: 2, textTransform: 'none' }}>
            {sending ? (uploading ? 'Uploading...' : 'Sending...') : 'Start Chat'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default StartChatPanel;
